import { create } from "zustand";
import {
  FilterGroup,
  FilterRule,
  PivotConfig,
  ReportColumn,
  ReportConfig,
  ReportGrain,
  ReportSheet,
  SchemaColumn,
  SchemaTable,
  SortRule,
} from "./types";
import { MOCK_SCHEMA } from "./mockSchema";
import {
  DateFormatKey,
  DEFAULT_DATE_FORMAT_KEY,
} from "../../../utils/dateFormatCatalog";

// ─── nanoid polyfill (not in deps) ────────────────────────────────────────────
function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Default sheet factory ────────────────────────────────────────────────────

function makeSheet(
  name: string,
  id?: string,
  grain: ReportGrain = "grant"
): ReportSheet {
  return {
    id: id ?? `sheet_${uid()}`,
    name,
    reportGrain: grain,
    columns: [],
    filters: [],
    groupBy: [],
    sortBy: [],
  };
}

// ─── Default config ───────────────────────────────────────────────────────────

const INITIAL_SHEET_ID = `sheet_${uid()}`;

const DEFAULT_CONFIG: ReportConfig = {
  name: "",
  description: "",
  sheets: [makeSheet("Sheet 1", INITIAL_SHEET_ID)],
  settings: {
    visibility: "private",
    refreshSchedule: "none",
    maskPii: false,
    dateFormat: DEFAULT_DATE_FORMAT_KEY,
  },
};

// ─── History cap ─────────────────────────────────────────────────────────────

const MAX_HISTORY = 50;

// ─── Store interface ──────────────────────────────────────────────────────────

interface ReportBuilderStore {
  // Schema
  schema: SchemaTable[];

  // Report config
  config: ReportConfig;

  // Active sheet
  activeSheetId: string;

  // UI state
  activeColumnId: string | null;
  isAdvancedMode: boolean;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  isFiltersPanelOpen: boolean;
  isSchemaCollapsed: boolean;
  isEditorCollapsed: boolean;

  // Undo/redo
  history: ReportConfig[];
  historyIndex: number;

  // ── Schema actions ──────────────────────────────────────────────────────────
  setSchema: (schema: SchemaTable[]) => void;

  // ── Sheet actions ───────────────────────────────────────────────────────────
  setActiveSheet: (sheetId: string) => void;
  addSheet: () => void;
  duplicateSheet: (sheetId: string) => void;
  removeSheet: (sheetId: string) => void;
  renameSheet: (sheetId: string, name: string) => void;
  reorderSheets: (fromIndex: number, toIndex: number) => void;

  // ── Config mutations (all push to history) ──────────────────────────────────
  setReportId: (id: string) => void;
  setReportName: (name: string) => void;
  setReportDescription: (description: string) => void;
  setVisibility: (v: ReportConfig["settings"]["visibility"]) => void;
  setRefreshSchedule: (s: ReportConfig["settings"]["refreshSchedule"]) => void;
  setMaskPii: (mask: boolean) => void;
  setDateFormat: (dateFormat: DateFormatKey) => void;
  setReportGrain: (grain: ReportGrain) => void;
  setPivot: (pivot: PivotConfig | null) => void;

  addColumn: (schemaCol: SchemaColumn, insertBeforeId?: string) => void;
  removeColumn: (id: string) => void;
  reorderColumns: (activeId: string, overId: string) => void;
  updateColumn: (id: string, patch: Partial<ReportColumn>) => void;
  addCustomColumn: () => void;

  addFilterGroup: () => void;
  removeFilterGroup: (groupId: string) => void;
  setFilterGroupLogic: (groupId: string, logic: "AND" | "OR") => void;
  addFilterRule: (groupId: string, rule: Omit<FilterRule, "id">) => void;
  updateFilterRule: (
    groupId: string,
    ruleId: string,
    patch: Partial<FilterRule>
  ) => void;
  removeFilterRule: (groupId: string, ruleId: string) => void;

  setGroupBy: (columns: string[]) => void;
  toggleGroupBy: (columnId: string) => void;

  addSortRule: (rule: SortRule) => void;
  removeSortRule: (column: string) => void;
  updateSortDirection: (column: string, direction: "asc" | "desc") => void;
  reorderSortRules: (fromIndex: number, toIndex: number) => void;

  // ── UI actions ──────────────────────────────────────────────────────────────
  setActiveColumn: (id: string | null) => void;
  setAdvancedMode: (v: boolean) => void;
  toggleFiltersPanel: () => void;
  toggleSchemaCollapsed: () => void;
  toggleEditorCollapsed: () => void;
  setIsSaving: (v: boolean) => void;
  markSaved: () => void;

  // ── Undo/redo ───────────────────────────────────────────────────────────────
  undo: () => void;
  redo: () => void;

  // ── Reset ───────────────────────────────────────────────────────────────────
  resetReport: () => void;
  loadReport: (config: ReportConfig) => void;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function pushHistory(state: ReportBuilderStore): Partial<ReportBuilderStore> {
  const next = state.history.slice(0, state.historyIndex + 1);
  next.push(JSON.parse(JSON.stringify(state.config)));
  if (next.length > MAX_HISTORY) next.shift();
  return { history: next, historyIndex: next.length - 1, isDirty: true };
}

/** Immutably patch the active sheet inside config.sheets */
function patchActiveSheet(
  state: ReportBuilderStore,
  patch: (sheet: ReportSheet) => ReportSheet
): { config: ReportConfig } & Partial<ReportBuilderStore> {
  const sheets = state.config.sheets.map((s) =>
    s.id === state.activeSheetId ? patch(s) : s
  );
  return {
    ...pushHistory(state),
    config: { ...state.config, sheets },
  };
}

/** Get the active sheet (fallback to first sheet) */
function getActiveSheet(state: {
  config: ReportConfig;
  activeSheetId: string;
}): ReportSheet {
  return (
    state.config.sheets.find((s) => s.id === state.activeSheetId) ??
    state.config.sheets[0]
  );
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useReportBuilderStore = create<ReportBuilderStore>((set, get) => ({
  schema: MOCK_SCHEMA,

  config: JSON.parse(JSON.stringify(DEFAULT_CONFIG)),
  activeSheetId: INITIAL_SHEET_ID,

  activeColumnId: null,
  isAdvancedMode: false,
  isDirty: false,
  isSaving: false,
  lastSavedAt: null,
  isFiltersPanelOpen: true,
  isSchemaCollapsed: false,
  isEditorCollapsed: false,

  history: [],
  historyIndex: -1,

  setSchema: (schema: SchemaTable[]) => set({ schema }),

  // ── Sheet management ────────────────────────────────────────────────────────
  setActiveSheet: (sheetId) =>
    set({ activeSheetId: sheetId, activeColumnId: null }),

  addSheet: () => {
    set((s) => {
      const newSheet = makeSheet(`Sheet ${s.config.sheets.length + 1}`);
      return {
        ...pushHistory(s),
        config: {
          ...s.config,
          sheets: [...s.config.sheets, newSheet],
        },
        activeSheetId: newSheet.id,
        activeColumnId: null,
      };
    });
  },

  duplicateSheet: (sheetId) => {
    set((s) => {
      const src = s.config.sheets.find((sh) => sh.id === sheetId);
      if (!src) return {};
      const copy: ReportSheet = {
        ...JSON.parse(JSON.stringify(src)),
        id: `sheet_${uid()}`,
        name: `${src.name} (copy)`,
      };
      return {
        ...pushHistory(s),
        config: {
          ...s.config,
          sheets: [...s.config.sheets, copy],
        },
        activeSheetId: copy.id,
        activeColumnId: null,
      };
    });
  },

  removeSheet: (sheetId) => {
    set((s) => {
      if (s.config.sheets.length <= 1) return {}; // keep at least one sheet
      const sheets = s.config.sheets.filter((sh) => sh.id !== sheetId);
      const nextActiveId =
        s.activeSheetId === sheetId ? sheets[0].id : s.activeSheetId;
      return {
        ...pushHistory(s),
        config: { ...s.config, sheets },
        activeSheetId: nextActiveId,
        activeColumnId: null,
      };
    });
  },

  renameSheet: (sheetId, name) => {
    set((s) => ({
      ...pushHistory(s),
      config: {
        ...s.config,
        sheets: s.config.sheets.map((sh) =>
          sh.id === sheetId ? { ...sh, name } : sh
        ),
      },
    }));
  },

  reorderSheets: (fromIndex, toIndex) => {
    set((s) => {
      const sheets = [...s.config.sheets];
      sheets.splice(toIndex, 0, sheets.splice(fromIndex, 1)[0]);
      return { ...pushHistory(s), config: { ...s.config, sheets } };
    });
  },

  // ── Report meta ─────────────────────────────────────────────────────────────
  setReportId: (id) => set((s) => ({ config: { ...s.config, id } })),

  setReportName: (name) =>
    set((s) => ({ ...pushHistory(s), config: { ...s.config, name } })),

  setReportDescription: (description) =>
    set((s) => ({ ...pushHistory(s), config: { ...s.config, description } })),

  setVisibility: (visibility) =>
    set((s) => ({
      ...pushHistory(s),
      config: { ...s.config, settings: { ...s.config.settings, visibility } },
    })),

  setRefreshSchedule: (refreshSchedule) =>
    set((s) => ({
      ...pushHistory(s),
      config: {
        ...s.config,
        settings: { ...s.config.settings, refreshSchedule },
      },
    })),

  setMaskPii: (maskPii) =>
    set((s) => ({
      ...pushHistory(s),
      config: { ...s.config, settings: { ...s.config.settings, maskPii } },
    })),

  setDateFormat: (dateFormat) =>
    set((s) => ({
      ...pushHistory(s),
      config: { ...s.config, settings: { ...s.config.settings, dateFormat } },
    })),

  setReportGrain: (reportGrain) =>
    set((s) => {
      const { config, activeSheetId } = s;
      return {
        ...pushHistory(s),
        config: {
          ...config,
          sheets: config.sheets.map((sh) =>
            sh.id === activeSheetId ? { ...sh, reportGrain } : sh
          ),
        },
      };
    }),

  setPivot: (pivot) =>
    set((s) => patchActiveSheet(s, (sh) => ({ ...sh, pivot: pivot ?? null }))),

  // ── Columns ─────────────────────────────────────────────────────────────────
  addColumn: (schemaCol, insertBeforeId) => {
    const activeSheet = getActiveSheet(get());
    const alreadyAdded = activeSheet.columns.some(
      (c) =>
        c.sourceTable === schemaCol.sourceTable &&
        c.sourceColumn === schemaCol.name
    );
    if (alreadyAdded) return;

    const newCol: ReportColumn = {
      id: `col_${uid()}`,
      sourceTable: schemaCol.sourceTable,
      sourceColumn: schemaCol.name,
      displayName: schemaCol.displayName,
      type: schemaCol.dataType,
      isCustom: false,
      format: null,
      aggregation: null,
      description: schemaCol.description,
    };

    set((s) =>
      patchActiveSheet(s, (sh) => {
        if (insertBeforeId) {
          const idx = sh.columns.findIndex((c) => c.id === insertBeforeId);
          if (idx !== -1) {
            const cols = [...sh.columns];
            cols.splice(idx, 0, newCol);
            return { ...sh, columns: cols };
          }
        }
        return { ...sh, columns: [...sh.columns, newCol] };
      })
    );
  },

  removeColumn: (id) => {
    set((s) => ({
      ...patchActiveSheet(s, (sh) => ({
        ...sh,
        columns: sh.columns.filter((c) => c.id !== id),
        groupBy: sh.groupBy.filter((g) => g !== id),
        sortBy: sh.sortBy.filter((sr) => sr.column !== id),
      })),
      activeColumnId: get().activeColumnId === id ? null : get().activeColumnId,
    }));
  },

  reorderColumns: (activeId, overId) => {
    set((s) => {
      const sheet = getActiveSheet(s);
      const cols = [...sheet.columns];
      const from = cols.findIndex((c) => c.id === activeId);
      const to = cols.findIndex((c) => c.id === overId);
      if (from === -1 || to === -1) return {};
      cols.splice(to, 0, cols.splice(from, 1)[0]);
      return patchActiveSheet(s, (sh) => ({ ...sh, columns: cols }));
    });
  },

  updateColumn: (id, patch) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        columns: sh.columns.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      }))
    );
  },

  addCustomColumn: () => {
    const newCol: ReportColumn = {
      id: `col_${uid()}`,
      sourceTable: null,
      sourceColumn: null,
      displayName: "Custom Column",
      type: "number",
      isCustom: true,
      formula: "",
      description: "",
      format: null,
      aggregation: null,
    };
    set((s) => ({
      ...patchActiveSheet(s, (sh) => ({
        ...sh,
        columns: [...sh.columns, newCol],
      })),
      activeColumnId: newCol.id,
    }));
  },

  // ── Filters ─────────────────────────────────────────────────────────────────
  addFilterGroup: () => {
    const group: FilterGroup = { id: `grp_${uid()}`, logic: "AND", rules: [] };
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: [...sh.filters, group],
      }))
    );
  },

  removeFilterGroup: (groupId) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: sh.filters.filter((g) => g.id !== groupId),
      }))
    );
  },

  setFilterGroupLogic: (groupId, logic) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: sh.filters.map((g) =>
          g.id === groupId ? { ...g, logic } : g
        ),
      }))
    );
  },

  addFilterRule: (groupId, rule) => {
    const newRule: FilterRule = { id: `rule_${uid()}`, ...rule };
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: sh.filters.map((g) =>
          g.id === groupId ? { ...g, rules: [...g.rules, newRule] } : g
        ),
      }))
    );
  },

  updateFilterRule: (groupId, ruleId, patch) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: sh.filters.map((g) =>
          g.id === groupId
            ? {
                ...g,
                rules: g.rules.map((r) =>
                  r.id === ruleId ? { ...r, ...patch } : r
                ),
              }
            : g
        ),
      }))
    );
  },

  removeFilterRule: (groupId, ruleId) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        filters: sh.filters.map((g) =>
          g.id === groupId
            ? { ...g, rules: g.rules.filter((r) => r.id !== ruleId) }
            : g
        ),
      }))
    );
  },

  // ── Group By ─────────────────────────────────────────────────────────────────
  setGroupBy: (columns) => {
    set((s) => patchActiveSheet(s, (sh) => ({ ...sh, groupBy: columns })));
  },

  toggleGroupBy: (columnId) => {
    set((s) => {
      const sheet = getActiveSheet(s);
      const next = sheet.groupBy.includes(columnId)
        ? sheet.groupBy.filter((id) => id !== columnId)
        : [...sheet.groupBy, columnId];
      return patchActiveSheet(s, (sh) => ({ ...sh, groupBy: next }));
    });
  },

  // ── Sort ─────────────────────────────────────────────────────────────────────
  addSortRule: (rule) => {
    set((s) => {
      const sheet = getActiveSheet(s);
      const existing = sheet.sortBy.filter((r) => r.column !== rule.column);
      return patchActiveSheet(s, (sh) => ({
        ...sh,
        sortBy: [...existing, rule],
      }));
    });
  },

  removeSortRule: (column) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        sortBy: sh.sortBy.filter((r) => r.column !== column),
      }))
    );
  },

  updateSortDirection: (column, direction) => {
    set((s) =>
      patchActiveSheet(s, (sh) => ({
        ...sh,
        sortBy: sh.sortBy.map((r) =>
          r.column === column ? { ...r, direction } : r
        ),
      }))
    );
  },

  reorderSortRules: (fromIndex, toIndex) => {
    set((s) => {
      const sheet = getActiveSheet(s);
      const rules = [...sheet.sortBy];
      rules.splice(toIndex, 0, rules.splice(fromIndex, 1)[0]);
      return patchActiveSheet(s, (sh) => ({ ...sh, sortBy: rules }));
    });
  },

  // ── UI ───────────────────────────────────────────────────────────────────────
  setActiveColumn: (id) => set({ activeColumnId: id }),
  setAdvancedMode: (v) => set({ isAdvancedMode: v }),
  toggleFiltersPanel: () =>
    set((s) => ({ isFiltersPanelOpen: !s.isFiltersPanelOpen })),
  toggleSchemaCollapsed: () =>
    set((s) => ({ isSchemaCollapsed: !s.isSchemaCollapsed })),
  toggleEditorCollapsed: () =>
    set((s) => ({ isEditorCollapsed: !s.isEditorCollapsed })),
  setIsSaving: (v) => set({ isSaving: v }),
  markSaved: () => set({ isDirty: false, lastSavedAt: new Date() }),

  // ── Undo/redo ────────────────────────────────────────────────────────────────
  undo: () => {
    set((s) => {
      if (s.historyIndex <= 0) return {};
      const nextIdx = s.historyIndex - 1;
      const config = JSON.parse(
        JSON.stringify(s.history[nextIdx])
      ) as ReportConfig;
      // Ensure activeSheetId still valid
      const sheetExists = config.sheets.some((sh) => sh.id === s.activeSheetId);
      return {
        config,
        historyIndex: nextIdx,
        isDirty: true,
        activeSheetId: sheetExists
          ? s.activeSheetId
          : config.sheets[0]?.id ?? s.activeSheetId,
      };
    });
  },

  redo: () => {
    set((s) => {
      if (s.historyIndex >= s.history.length - 1) return {};
      const nextIdx = s.historyIndex + 1;
      const config = JSON.parse(
        JSON.stringify(s.history[nextIdx])
      ) as ReportConfig;
      const sheetExists = config.sheets.some((sh) => sh.id === s.activeSheetId);
      return {
        config,
        historyIndex: nextIdx,
        isDirty: true,
        activeSheetId: sheetExists
          ? s.activeSheetId
          : config.sheets[0]?.id ?? s.activeSheetId,
      };
    });
  },

  // ── Reset / Load ─────────────────────────────────────────────────────────────
  resetReport: () => {
    const freshConfig = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
    set({
      config: freshConfig,
      activeSheetId: freshConfig.sheets[0].id,
      history: [],
      historyIndex: -1,
      isDirty: false,
      lastSavedAt: null,
      activeColumnId: null,
    });
  },

  loadReport: (config) => {
    set({
      config: JSON.parse(JSON.stringify(config)),
      activeSheetId: config.sheets[0]?.id ?? INITIAL_SHEET_ID,
      history: [JSON.parse(JSON.stringify(config))],
      historyIndex: 0,
      isDirty: false,
      lastSavedAt: null,
      activeColumnId: null,
    });
  },
}));
